import { useEffect, useState } from 'react';
import { GitDiff } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { engApi } from '../engApi';
import { VL, versionErrorText } from './digestVersionI18n';
import { jstDateTime } from '@/utils/jstDate';

const MGMT_LABEL = {
  ja: { executive_summary: 'エグゼクティブサマリー', accomplishments: '主な成果', active_projects: '進行中のプロジェクト', risks: 'リスクの兆候', blocked: 'ブロック中の活動', lessons: '教訓・学び', upcoming: '来週の予定', themes: '戦略テーマ' },
  en: { executive_summary: 'Executive summary', accomplishments: 'Accomplishments', active_projects: 'Active projects', risks: 'Risks', blocked: 'Blocked', lessons: 'Lessons', upcoming: 'Upcoming', themes: 'Themes' },
};

function ListDiff({ d, L }) {
  return (
    <div className="text-[11.5px] space-y-0.5">
      {d.added.map((x) => <div key={`a${x}`} className="text-emerald-700">＋ {x}</div>)}
      {d.removed.map((x) => <div key={`r${x}`} className="text-rose-700 line-through">－ {x}</div>)}
      {d.unchanged?.length > 0 && <div className="text-zinc-400">{L.unchanged}: {d.unchanged.length}</div>}
    </div>
  );
}

function TextDiff({ d }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-[11.5px]">
      <div className="rounded bg-rose-50 border border-rose-100 p-2 text-rose-900 whitespace-pre-wrap">{d.a || '—'}</div>
      <div className="rounded bg-emerald-50 border border-emerald-100 p-2 text-emerald-900 whitespace-pre-wrap">{d.b || '—'}</div>
    </div>
  );
}

function KeyedDiff({ k, L, title }) {
  return (
    <section data-testid={`digest-compare-${title}`}>
      <h4 className="text-[12px] font-bold text-zinc-700 mt-3 mb-1">{L[title]} <span className="text-zinc-400 font-normal">({k.a_count} → {k.b_count})</span></h4>
      {k.added.length > 0 && <p className="text-[11.5px] text-emerald-700">＋ {L.added}: {k.added.join('、')}</p>}
      {k.removed.length > 0 && <p className="text-[11.5px] text-rose-700">－ {L.removed}: {k.removed.join('、')}</p>}
      {k.changed.map((c) => (
        <div key={c.key} className="mt-1 rounded border border-zinc-200 p-2">
          <p className="text-[12px] font-semibold text-zinc-800">{c.key}</p>
          {Object.entries(c.fields).map(([f, d]) => (
            <div key={f} className="mt-1">
              <p className="text-[10.5px] uppercase tracking-wider text-zinc-400">{f}</p>
              {d.kind === 'list' ? <ListDiff d={d} L={L} /> : <TextDiff d={d} />}
            </div>
          ))}
        </div>
      ))}
      {k.added.length === 0 && k.removed.length === 0 && k.changed.length === 0 && <p className="text-[11.5px] text-zinc-400">{L.unchanged}</p>}
    </section>
  );
}

export function DigestCompareDialog({ sid, pair, onClose, lang }) {
  const L = VL[lang] || VL.ja;
  const ML = MGMT_LABEL[lang] || MGMT_LABEL.ja;
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (!pair) { setRes(null); return; }
    setRes(null);
    engApi.compareDigestVersions(sid, pair[0], pair[1]).then(setRes)
      .catch((e) => { toast.error(versionErrorText(e, lang, L.compare)); onClose(); });
  }, [sid, pair]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={!!pair} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="digest-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitDiff size={16} weight="duotone" className="text-indigo-600" /> {L.compare}</DialogTitle>
        </DialogHeader>
        {!res ? <div className="py-10 text-center text-sm text-zinc-400">{L.comparing}</div> : (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2 text-[12px]">
              {['a', 'b'].map((side) => (
                <div key={side} className={`rounded-lg border p-2 ${side === 'a' ? 'border-rose-200 bg-rose-50/40' : 'border-emerald-200 bg-emerald-50/40'}`} data-testid={`digest-compare-head-${side}`}>
                  <p className="font-bold text-zinc-800">v{res[side].version_number} <span className="font-normal text-zinc-500">· {L[res[side].lifecycle] || res[side].lifecycle} · {res[side].status}</span></p>
                  <p className="text-[11px] text-zinc-500">{jstDateTime(res[side].generated_at)} · {res[side].generated_by}</p>
                </div>
              ))}
            </div>
            {res.summary.identical_content && <p className="rounded bg-zinc-50 border border-zinc-200 p-2 text-[12px] text-zinc-600" data-testid="digest-compare-identical">{L.identical}</p>}
            <section data-testid="digest-compare-metadata">
              <h4 className="text-[12px] font-bold text-zinc-700 mt-2 mb-1">{L.metadata}</h4>
              <table className="w-full text-[11.5px]">
                <tbody>
                  {res.metadata.filter((m) => m.changed).map((m) => (
                    <tr key={m.field} className="border-t border-zinc-100">
                      <td className="py-0.5 text-zinc-500 w-40">{m.field}</td>
                      <td className="py-0.5 text-rose-700">{JSON.stringify(m.a)}</td>
                      <td className="py-0.5 text-emerald-700">{JSON.stringify(m.b)}</td>
                    </tr>
                  ))}
                  {res.metadata.every((m) => !m.changed) && <tr><td className="text-zinc-400">{L.unchanged}</td></tr>}
                </tbody>
              </table>
            </section>
            <section data-testid="digest-compare-management">
              <h4 className="text-[12px] font-bold text-zinc-700 mt-3 mb-1">{L.management}</h4>
              {Object.entries(res.management).filter(([, d]) => d.changed).map(([k, d]) => (
                <div key={k} className="mt-1">
                  <p className="text-[11px] font-semibold text-zinc-600">{ML[k] || k}</p>
                  {d.kind === 'list' ? <ListDiff d={d} L={L} /> : <TextDiff d={d} />}
                </div>
              ))}
              {Object.values(res.management).every((d) => !d.changed) && <p className="text-[11.5px] text-zinc-400">{L.unchanged}</p>}
            </section>
            <KeyedDiff k={res.engineers} L={L} title="engineers" />
            <KeyedDiff k={res.projects} L={L} title="projects" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
